import type { CSSProperties } from "react";
import styles from "./HeroAccent.module.css";

export type AccentVariant = "waves" | "drift" | "strands" | "bloom" | "arcs";

type HeroAccentProps = {
  variant: AccentVariant;
};

/**
 * Decorative SVG accents shown beside hero text. The SVG is designed to fill
 * its container edge-to-edge using preserveAspectRatio="xMidYMid slice" so
 * paths bleed off the visible bounds for an "infinite flow" feel. All variants
 * are purely decorative — no figurative meaning intended.
 *
 * The `waves` variant has a continuous gentle vertical drift on each line for
 * a calming "moving water" feel; the wobble filter is omitted there because
 * the screen-space displacement would shimmer against the animation.
 */
export default function HeroAccent({ variant }: HeroAccentProps) {
  const useWobble = variant !== "waves";
  // Bloom anchors to the top-right of the container so its shapes press
  // flush against the hero's top and right edges (with the bottom-left
  // sliced off by the slice fit).
  const preserve = variant === "bloom" ? "xMaxYMin slice" : "xMidYMid slice";

  return (
    <div className={styles.wrap} aria-hidden="true">
      <svg
        className={styles.svg}
        viewBox="0 0 400 600"
        preserveAspectRatio={preserve}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter id="wobble" x="-2%" y="-2%" width="104%" height="104%">
            <feTurbulence type="fractalNoise" baseFrequency="0.012" numOctaves="2" seed="3" />
            <feDisplacementMap in="SourceGraphic" scale="2.2" />
          </filter>
        </defs>

        <g
          filter={useWobble ? "url(#wobble)" : undefined}
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        >
          {variant === "waves" ? <Waves /> : null}
          {variant === "drift" ? <Drift /> : null}
          {variant === "strands" ? <Strands /> : null}
          {variant === "bloom" ? <Bloom /> : null}
          {variant === "arcs" ? <Arcs /> : null}
        </g>
      </svg>
    </div>
  );
}

/**
 * Horizontal flowing waves stacked top-to-bottom. Lines extend past the left
 * and right edges so they read as continuous currents rather than fixed strokes.
 * Each line has a slow vertical bob (different duration & phase per line) so
 * the field reads like gentle moving water.
 */
function Waves() {
  const lines = [
    { y: 30, w: 1.7, o: 1 },
    { y: 90, w: 1.6, o: 0.92 },
    { y: 150, w: 1.5, o: 0.84 },
    { y: 210, w: 1.5, o: 0.76 },
    { y: 270, w: 1.4, o: 0.68 },
    { y: 330, w: 1.4, o: 0.6 },
    { y: 390, w: 1.3, o: 0.52 },
    { y: 450, w: 1.3, o: 0.45 },
    { y: 510, w: 1.2, o: 0.38 },
    { y: 570, w: 1.2, o: 0.3 },
  ];
  return (
    <>
      {lines.map((l, i) => {
        const phase = i % 2 === 0 ? 0 : 40;
        // Per-wave timing: slightly different duration and a negative delay
        // so each wave starts mid-cycle (so they're never in lock-step).
        const style = {
          "--wave-dur": `${6 + (i % 4) * 0.7}s`,
          "--wave-delay": `${-(i * 0.55).toFixed(2)}s`,
          "--wave-amp": `${4 + (i % 3)}px`,
        } as CSSProperties;
        return (
          <path
            key={l.y}
            className={styles.wave}
            style={style}
            d={`M -40 ${l.y} C 60 ${l.y - 26 + phase * 0.2} 140 ${l.y + 32 - phase * 0.1} 220 ${l.y - 20} C 300 ${l.y - 50} 360 ${l.y + 28} 440 ${l.y + 6}`}
            strokeWidth={l.w}
            opacity={l.o}
          />
        );
      })}
    </>
  );
}

/**
 * Long sweeping diagonal currents from upper-left toward lower-right (and a
 * couple in the opposite direction for cross-flow). Each curve enters and exits
 * off-canvas.
 */
function Drift() {
  return (
    <>
      <path
        d="M -40 60 C 80 90 180 200 260 320 C 320 410 380 510 460 600"
        strokeWidth="1.7"
      />
      <path
        d="M -40 140 C 100 160 200 280 280 410 C 340 510 400 580 460 640"
        strokeWidth="1.6"
        opacity="0.88"
      />
      <path
        d="M -40 240 C 110 240 220 360 300 490 C 360 580 410 630 460 680"
        strokeWidth="1.5"
        opacity="0.74"
      />
      <path
        d="M -40 380 C 110 360 240 460 320 580 C 370 650 410 680 460 720"
        strokeWidth="1.4"
        opacity="0.6"
      />
      <path
        d="M -40 520 C 130 510 260 590 340 660 C 380 700 420 720 460 740"
        strokeWidth="1.3"
        opacity="0.48"
      />
      {/* a couple of counter-direction curves for visual depth */}
      <path
        d="M 460 80 C 360 120 280 240 220 360 C 160 480 100 540 -40 580"
        strokeWidth="1.4"
        opacity="0.5"
      />
      <path
        d="M 460 220 C 380 240 320 340 270 440 C 220 540 140 600 -40 660"
        strokeWidth="1.3"
        opacity="0.38"
      />
    </>
  );
}

/**
 * Vertical flowing strands top-to-bottom, each with its own wave amplitude and
 * phase so they read like loose silk or seaweed.
 */
function Strands() {
  const strands = [
    { x: 40, amp: 14, phase: 0, w: 1.5, o: 0.85 },
    { x: 95, amp: 22, phase: 60, w: 1.6, o: 1 },
    { x: 150, amp: 16, phase: 120, w: 1.5, o: 0.92 },
    { x: 205, amp: 24, phase: 180, w: 1.6, o: 0.85 },
    { x: 260, amp: 18, phase: 240, w: 1.5, o: 0.78 },
    { x: 315, amp: 22, phase: 300, w: 1.4, o: 0.7 },
    { x: 370, amp: 16, phase: 360, w: 1.3, o: 0.55 },
  ];
  return (
    <>
      {strands.map((s) => {
        const a = s.amp;
        // build a path that meanders top-to-bottom with a sine-like motion
        const d =
          `M ${s.x} -40 ` +
          `C ${s.x - a} 80 ${s.x + a} 180 ${s.x} 280 ` +
          `C ${s.x - a} 380 ${s.x + a} 480 ${s.x} 580 ` +
          `C ${s.x - a * 0.6} 640 ${s.x + a * 0.6} 660 ${s.x} 700`;
        return <path key={s.x} d={d} strokeWidth={s.w} opacity={s.o} />;
      })}
    </>
  );
}

/**
 * Soft asymmetric blob shapes biased toward the top-right corner, with
 * connecting flow lines threading between them. Designed for a top-right
 * corner-anchored slice so the visual weight presses against the hero's
 * top and right edges.
 */
function Bloom() {
  return (
    <>
      {/* connecting flow-lines threading through the blooms */}
      <path
        d="M -40 120 C 80 90 200 220 280 290 C 340 350 380 400 440 440"
        strokeWidth="1.4"
        opacity="0.45"
      />
      <path
        d="M -40 280 C 120 260 240 360 320 440 C 380 500 420 530 460 560"
        strokeWidth="1.3"
        opacity="0.32"
      />
      <path
        d="M 460 60 C 380 80 320 160 280 250 C 240 340 180 410 -40 500"
        strokeWidth="1.3"
        opacity="0.4"
      />
      <path
        d="M 240 -40 C 280 80 320 200 360 320 C 390 410 410 470 440 540"
        strokeWidth="1.2"
        opacity="0.32"
      />

      {/* blob A — large, anchored in the top-right (extends past top + right edges) */}
      <path
        d="M 230 -40 C 360 -30 460 50 460 160 C 460 260 360 320 250 300 C 160 285 130 200 160 110 C 175 60 195 20 230 -40 Z"
        fill="currentColor"
        stroke="none"
        opacity="0.2"
      />
      <path
        d="M 230 -40 C 360 -30 460 50 460 160 C 460 260 360 320 250 300 C 160 285 130 200 160 110 C 175 60 195 20 230 -40 Z"
        strokeWidth="1.4"
        opacity="0.45"
      />

      {/* blob B — medium, upper-mid (overlaps blob A) */}
      <path
        d="M 80 80 C 180 60 250 110 250 190 C 250 270 180 310 100 300 C 30 290 0 230 20 160 C 35 120 55 95 80 80 Z"
        fill="currentColor"
        stroke="none"
        opacity="0.16"
      />
      <path
        d="M 80 80 C 180 60 250 110 250 190 C 250 270 180 310 100 300 C 30 290 0 230 20 160 C 35 120 55 95 80 80 Z"
        strokeWidth="1.3"
        opacity="0.38"
      />

      {/* blob C — smaller, middle-right (extends past right edge) */}
      <path
        d="M 280 320 C 380 310 460 360 460 430 C 460 500 380 530 310 520 C 250 510 220 460 240 410 C 255 370 265 340 280 320 Z"
        fill="currentColor"
        stroke="none"
        opacity="0.14"
      />
      <path
        d="M 280 320 C 380 310 460 360 460 430 C 460 500 380 530 310 520 C 250 510 220 460 240 410 C 255 370 265 340 280 320 Z"
        strokeWidth="1.2"
        opacity="0.3"
      />

      {/* small accent dots for some texture in upper area */}
      <circle cx="370" cy="210" r="3" fill="currentColor" stroke="none" opacity="0.45" />
      <circle cx="200" cy="40" r="2.5" fill="currentColor" stroke="none" opacity="0.4" />
    </>
  );
}

/**
 * Series of large flowing arcs sweeping across the canvas — like ribbons
 * caught in motion. Each arc bleeds off two edges.
 */
function Arcs() {
  return (
    <>
      <path d="M -40 80 C 60 -40 260 -40 460 80" strokeWidth="1.7" />
      <path d="M -40 180 C 60 60 260 60 460 180" strokeWidth="1.6" opacity="0.88" />
      <path d="M -40 280 C 60 160 260 160 460 280" strokeWidth="1.5" opacity="0.76" />
      <path d="M -40 380 C 60 260 260 260 460 380" strokeWidth="1.4" opacity="0.64" />
      <path d="M -40 480 C 60 360 260 360 460 480" strokeWidth="1.3" opacity="0.52" />
      <path d="M -40 580 C 60 460 260 460 460 580" strokeWidth="1.3" opacity="0.42" />
      <path d="M -40 680 C 60 560 260 560 460 680" strokeWidth="1.2" opacity="0.32" />
      {/* a few counter arcs */}
      <path d="M -40 240 C 100 360 280 360 460 240" strokeWidth="1.3" opacity="0.4" />
      <path d="M -40 480 C 100 600 280 600 460 480" strokeWidth="1.3" opacity="0.35" />
    </>
  );
}
